import type {
  Changeset,
  CountChangeset,
  CreateChangeset,
  FieldEdits,
  RecordRef,
  UpdateChangeset,
} from './sync'

/**
 * How a change joins a queue that already holds one about the same thing
 * (issue #70).
 *
 * The queue used to append, full stop. Edit one lantern's quantity three times
 * in a cellar and it held three changesets, each with a `base` taken from the
 * device's own previous unsent edit:
 *
 *   #1 quantity {base: 4, value: 5}   ← 4 is what the server holds
 *   #2 quantity {base: 5, value: 6}   ← 5 is a number the server never saw
 *   #3 quantity {base: 6, value: 7}
 *
 * Applied in order that is fine, and `SyncApplier` does keep order per record.
 * It stops being fine the moment anybody disagrees: somebody else moves the
 * quantity to 9, #1 conflicts, #2 and #3 are blocked behind it, and the walker
 * who answers "keep theirs" is asked the same question again — against a base of
 * 5, a value that only ever existed inside this device. Their answer is undone
 * by their own earlier keystrokes.
 *
 * So changes about one record fold into one changeset: **the earliest base, the
 * latest value.** The base is what *the server* was believed to hold, and the
 * intermediate values were never the server's. One changeset, one base, one
 * question, and an answer that stays answered. It is the rule `reorder` has had
 * since issue #27, generalised — three drags were always the third drag.
 *
 * This lives in `@mads/shared` with the changeset types for the reason those do:
 * React Native adopts the queue and would otherwise need a second copy of the
 * only part of it that decides what a walker gets asked.
 */

/**
 * Everything a changeset is *about*, for deciding what it can fold into.
 *
 * The same shape as `SyncApplier::blockingKeys()` server-side, and for a related
 * reason: a count is about its line **and** about its walk, so a walk being
 * finished in between stops a later count folding backwards past it, exactly as
 * a stopped walk blocks its counts on replay.
 *
 * A `reorder` is about no record — it is the whole order — and has its own
 * supersede rule below.
 */
function touchKeys(changeset: Changeset): string[] {
  switch (changeset.kind) {
    case 'reorder':
      return []
    case 'create':
      // Through `refKey` rather than the bare client id, so a create and the
      // edits that point at it by `{clientId}` produce the same key.
      return [`${changeset.entity}:${refKey({ clientId: changeset.clientId })}`]
    case 'count':
      return [walkKey(changeset), lineKey(changeset)]
    default:
      return [`${changeset.entity}:${refKey(changeset.target)}`]
  }
}

function refKey(ref: RecordRef): string {
  return 'id' in ref ? `id:${ref.id}` : `client:${ref.clientId}`
}

function walkKey(changeset: CountChangeset): string {
  return `stocktake:${refKey(changeset.target)}`
}

/**
 * A line is named by its item, not by its line id (issue #64, step 3) — line ids
 * are handed out at `start()`, so a walk begun in a cellar has none. The same
 * naming has to be what decides whether two counts are about the same shelf.
 */
function lineKey(changeset: CountChangeset): string {
  return `${walkKey(changeset)}:item:${refKey(changeset.item)}`
}

/** The record a changeset is primarily about — a count's is its line. */
function primaryKey(changeset: Changeset): string | null {
  const keys = touchKeys(changeset)

  return keys.length === 0 ? null : (keys[keys.length - 1] as string)
}

/**
 * Adds a change to a queue, folding it into what is already there where that is
 * the same change said again.
 *
 * `sent` is the ids that have already gone to the server at least once, and
 * nothing in it is ever merged into — see `canMergeInto` for why that matters
 * more than it looks.
 */
export function enqueueChangeset(
  changesets: readonly Changeset[],
  incoming: Changeset,
  sent: ReadonlySet<string> = new Set(),
): Changeset[] {
  // The walking order is one whole-order write (issue #27). Replaying three
  // drags as three writes is the third one with two wasted round trips in front
  // of it, so a new one supersedes any queued one outright.
  if (incoming.kind === 'reorder') {
    return [...changesets.filter((queued) => queued.kind !== 'reorder'), incoming]
  }

  // A photograph is last-writer-wins by design (issue #64, step 4): "your
  // picture or theirs" is a question with no answer, and that applies to two of
  // this device's own as much as to anybody else's. So an earlier queued one for
  // the same record goes, which also stops a walker who photographs the same
  // lantern twice spending two of their hundred.
  if (incoming.kind === 'photograph') {
    const key = primaryKey(incoming)

    return [
      ...changesets.filter(
        (queued) =>
          queued.kind !== 'photograph' || sent.has(queued.id) || primaryKey(queued) !== key,
      ),
      incoming,
    ]
  }

  const target = lastTouching(changesets, incoming)
  const merged = target === null ? null : merge(target, incoming, sent)

  if (merged === null) return [...changesets, incoming]

  // In place. The changeset keeps its id — so an answer already given about it,
  // and the server's record of it, still refer to the same thing — and its
  // position, because an item created into a location created five minutes
  // earlier only resolves if the two are still in that order.
  return changesets.map((queued) => (queued.id === merged.id ? merged : queued))
}

/**
 * The last queued change touching anything this one touches.
 *
 * "Last touching" rather than "last of the same kind" is what makes the
 * intervening cases fall out for free: a bin queued between two edits of one
 * lantern means the last thing touching it is the bin, so the second edit is
 * appended rather than folded backwards over the bin.
 */
function lastTouching(changesets: readonly Changeset[], incoming: Changeset): Changeset | null {
  const keys = new Set(touchKeys(incoming))

  if (keys.size === 0) return null

  for (let index = changesets.length - 1; index >= 0; index--) {
    const queued = changesets[index] as Changeset

    if (touchKeys(queued).some((key) => keys.has(key))) return queued
  }

  return null
}

function merge(target: Changeset, incoming: Changeset, sent: ReadonlySet<string>): Changeset | null {
  if (!canMergeInto(target, sent)) return null
  if (primaryKey(target) !== primaryKey(incoming)) return null

  if (incoming.kind === 'update') {
    // The entity check is what tells a record's create from a walk's start:
    // both are `kind: 'create'`, and only the first has fields to fold into.
    // Their keys could not collide anyway — `RecordEntity` and `SyncEntity` are
    // separate types precisely so a changeset cannot cross them — but saying so
    // here is what lets the compiler agree.
    if (target.kind === 'create' && target.entity !== 'stocktake') {
      return foldIntoCreate(target, incoming)
    }

    if (target.kind === 'update') return mergeUpdates(target, incoming)

    return null
  }

  if (incoming.kind === 'count' && target.kind === 'count') {
    return mergeCounts(target, incoming)
  }

  // Everything else is appended, and two of those are deliberate:
  //
  //   - **A create never folds into anything.** It has a fresh client id and
  //     nothing before it is about the same record.
  //   - **Create-then-bin is two changesets, both applied.** Folding them away
  //     would mean a record made in a cellar and binned five minutes later never
  //     reaching the server, and therefore never reaching the trash — where an
  //     item binned online sits, whole and restorable (issue #8). The walker who
  //     binned it by mistake must find it in the same place either way.
  return null
}

/**
 * Whether a queued changeset may still be folded into.
 *
 * **Never one that has already been sent**, and this is the subtle one.
 * `SyncApplier` answers a repeat of an applied changeset id straight out of
 * `sync_record` without running it — which is exactly right for the case it
 * exists for, a batch that applied and whose answer was lost in a stairwell.
 * Merge new fields into such an id and the server would answer the *old* result
 * and the new edit would be silently dropped. So a sent changeset is left alone
 * and the change is appended after it, which is what the queue did for
 * everything before this.
 */
function canMergeInto(target: Changeset, sent: ReadonlySet<string>): boolean {
  return !sent.has(target.id)
}

/**
 * An edit to a record the server has never heard of is just what the create
 * should have said.
 *
 * There is no base to keep: nothing existed to disagree with. The create keeps
 * **its own timestamp**, unlike a merged edit below — the record came into being
 * when it came into being, and a correction to its name is not a later act of
 * creation.
 */
function foldIntoCreate(target: CreateChangeset, incoming: UpdateChangeset): CreateChangeset {
  const fields: Record<string, unknown> = { ...target.fields }

  for (const [field, edit] of Object.entries(incoming.fields)) {
    fields[field] = edit.value
  }

  return { ...target, fields }
}

/**
 * Two edits of one record, as one.
 *
 * The **later** timestamp, because what is being recorded is the value the
 * walker settled on and `AuditLogEntry.occurredAt` should say when they settled
 * on it — and because a fortnight (`SYNC_MAX_AGE_DAYS`) should be measured from
 * the last time somebody touched this, not the first.
 */
function mergeUpdates(target: UpdateChangeset, incoming: UpdateChangeset): UpdateChangeset {
  return {
    ...target,
    at: incoming.at,
    fields: mergeFields(target.fields, incoming.fields),
  }
}

function mergeCounts(target: CountChangeset, incoming: CountChangeset): CountChangeset {
  return {
    ...target,
    at: incoming.at,
    // What the sheet in the walker's hand said. The first reading of it is the
    // one to keep, for the same reason the base is: it is what they counted
    // against, and it cannot have changed between two counts of one shelf.
    expectedQuantity: target.expectedQuantity,
    fields: mergeFields(target.fields, incoming.fields),
  }
}

/**
 * Earliest base, latest value, per field.
 *
 * A field the earlier changeset never touched takes the incoming base, which was
 * read off a cache the earlier changeset had not moved — so it is still what the
 * server was believed to hold. A field it did touch keeps the base it already
 * has, which may be one the walker has since **resolved** a conflict about: that
 * answer must survive them typing in the box again, or answering a conflict
 * would be undone by carrying on working.
 */
function mergeFields(existing: FieldEdits, incoming: FieldEdits): FieldEdits {
  const fields: Record<string, { base: unknown; value: unknown }> = { ...existing }

  for (const [field, edit] of Object.entries(incoming)) {
    fields[field] = { base: field in fields ? (fields[field] as { base: unknown }).base : edit.base, value: edit.value }
  }

  return fields
}
